import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';
import type { ScribeStatus } from '../services/ai-speech-to-text.service';

const MESSAGES = {
  connecting: {
    title: 'Getting your microphone ready…',
    detail: 'Please wait until you see “Listening” before speaking.',
  },
  recording: {
    title: 'Listening. You can speak now.',
    detail: 'Speak naturally. Your words will appear in the message field.',
  },
  stopping: {
    title: 'Finishing your dictation…',
    detail: 'Keeping your final words. Your microphone is off.',
  },
};

@Component({
  selector: 'app-voice-input-status',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      class="voice-status"
      role="status"
      aria-live="polite"
      aria-atomic="true"
      [class.listening]="status() === 'recording'"
    >
      @if (message(); as copy) {
      <span class="voice-indicator" aria-hidden="true">
        @if (status() === 'recording') {
        <span class="voice-bar"></span><span class="voice-bar"></span
        ><span class="voice-bar"></span>
        } @else {
        <span class="voice-spinner"></span>
        }
      </span>
      <span class="voice-copy"
        ><strong>{{ copy.title }}</strong
        ><span>{{ copy.detail }}</span></span
      >
      }
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
      }
      .voice-status {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 11px 14px;
        margin-bottom: 10px;
        border: 1px solid #dce5ef;
        border-radius: 12px;
        background: #f0f5fa;
        color: #304a6c;
        animation: voice-arrive 180ms ease-out;
      }
      .voice-status:empty {
        display: none;
      }
      .voice-status.listening {
        background: #edf6f7;
        border-color: #cde3e7;
        color: #244b5b;
      }
      .voice-copy {
        display: flex;
        flex-direction: column;
        gap: 2px;
        min-width: 0;
        line-height: 1.45;
      }
      strong {
        font-size: 0.875rem;
        font-weight: 600;
      }
      .voice-copy > span {
        font-size: 0.75rem;
        color: #60738a;
      }
      .voice-indicator {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 3px;
        width: 28px;
        height: 28px;
        flex: none;
      }
      .voice-spinner {
        width: 18px;
        height: 18px;
        border: 2px solid #a9bdce;
        border-top-color: #385c7e;
        border-radius: 50%;
        animation: voice-spin 900ms linear infinite;
      }
      .voice-bar {
        width: 3px;
        height: 16px;
        background: #3d8192;
        border-radius: 3px;
        animation: voice-listen 1.2s ease-in-out infinite;
        transform-origin: center;
      }
      .voice-bar:nth-child(2) {
        height: 22px;
        animation-delay: -0.4s;
      }
      .voice-bar:nth-child(3) {
        animation-delay: -0.8s;
      }
      @keyframes voice-arrive {
        from {
          opacity: 0;
          transform: translateY(3px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      @keyframes voice-spin {
        to {
          transform: rotate(360deg);
        }
      }
      @keyframes voice-listen {
        0%,
        100% {
          transform: scaleY(0.5);
        }
        50% {
          transform: scaleY(1);
        }
      }
      @media (prefers-reduced-motion: reduce) {
        .voice-status,
        .voice-spinner,
        .voice-bar {
          animation: none;
        }
      }
    `,
  ],
})
export class VoiceInputStatusComponent {
  status = input<ScribeStatus>('idle');
  readonly message = computed(() => {
    const status = this.status();
    return status === 'connecting' ||
      status === 'recording' ||
      status === 'stopping'
      ? MESSAGES[status]
      : null;
  });
}
